import React, { useState, useContext } from 'react';

// Styles
import styles from './ButtonsCreatePoll.module.css';
import { ReactComponent as BellSvg } from '../../../assets/icons/bell.svg';

// Context
import ScoresContext from '../../../context/ScoresContext';

// Components
import Modal from '../../UI/Modal/Modal';
import TryPollDialog from '../TryPollDialog/TryPollDialog';
import ScoresAlertsDialog from '../ScoresAlertsDialog/ScoresAlertsDialog';

// Interfaces
import { Poll } from '../../../interfaces/Poll';
import { PollDispatch } from '../../../interfaces/Reducers';

// Language
import { useTranslation } from 'react-i18next';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import { IAlertRule, Score } from '../../../interfaces/Score';
import ButtonForm from '../ButtonForm/ButtonForm';


const ButtonsCreatePoll: React.FC<{
    poll: Poll,
    modified: boolean,
    dispatchPoll: React.Dispatch<PollDispatch>
    handleSave: (redirect?: string, callbackValidationErr?: () => void) => void
}> = ({ poll, modified, dispatchPoll, handleSave }) => {

    const { t } = useTranslation();

    const [showTryPollDialog, setShowTryPollDialog] = useState<boolean>(false);
    const [showScoresDialog, setShowScoresDialog] = useState<boolean>(false);

    const { scores, setScores } = useContext(ScoresContext);

    const score = scores?.find(s => s.inquiry_id === poll.id);


    const questions = poll.questions || [];

    const confirmSaveFromScores = () => {
        handleSave(undefined, () => setShowScoresDialog(false));
    };

    const addAlerts = (alerts: IAlertRule[]) => dispatchPoll({ type: 'ADD_ALERTS', payload: alerts });

    const addScore = (score: Score | number) => {
        if (typeof score === 'number') setScores(oldScores => oldScores === null ?
            null : oldScores.filter(sc => sc.id !== score));

        else setScores(oldScores => {
            if (oldScores === null) return null;
            const scoreFound = oldScores.find(sc => sc.id === score.id);
            if (scoreFound) {
                return oldScores.map(sc => {
                    if (sc.id !== score.id) return sc;
                    const newScore = { ...scoreFound, ...score };
                    return newScore;
                });
            }
            else return [...oldScores, score];
        });
    };

    return (
        <>
            <div className={styles.createPollActions}>
                <ButtonForm onClick={() => setShowScoresDialog(true)}>
                    {t('scores_and_alerts')}
                    <BellSvg />
                </ButtonForm>
                <ButtonForm
                    onClick={() => setShowTryPollDialog(true)}
                    disabled={questions.length === 0}
                >
                    {t('try_poll')}
                </ButtonForm>
                <ButtonForm
                    className={styles.savePoll}
                    onClick={() => handleSave()}
                    disabled={modified === false}
                >
                    {t('save_poll')}
                </ButtonForm>
            </div>
            <Modal show={showScoresDialog} setShowModal={setShowScoresDialog}>
                {modified ?
                    <div className='dialog' style={{ textAlign: 'center' }}>
                        <h1 className='dialogTitle'>{t('modify_scores_save_poll')}</h1>
                        <CancelConfirmButtons
                            confirmName={t('save_continue') as string}
                            onCancel={() => setShowScoresDialog(false)}
                            onConfirm={confirmSaveFromScores}
                        />
                    </div>
                    :
                    <ScoresAlertsDialog
                        defaultScores={score?.score_rules || []}
                        scoreID={score?.id}
                        defaultAlerts={poll.inquiry_alerts || []}
                        poll={poll}
                        setShow={setShowScoresDialog}
                        addAlerts={addAlerts}
                        addScore={addScore}
                    />
                }
            </Modal>
            <Modal show={showTryPollDialog} setShowModal={setShowTryPollDialog}>
                <TryPollDialog poll={poll} questions={questions} score={score} showModal={setShowTryPollDialog} />
            </Modal>
        </>
    );
};

export default ButtonsCreatePoll;